const BlockType = require('../../extension-support/block-type');
const ArgumentType = require('../../extension-support/argument-type');
const Scratch3LooksBlocks = require('../../blocks/scratch3_looks');
const languageNames = require('scratch-translate-extension-languages');
const formatMessage = require('format-message');
const Cast = require('../../util/cast');
const log = require('../../util/log');
const {fetchWithTimeout} = require('../../util/fetch-with-timeout');
const nlp = require('compromise');

const serverTimeoutMs = 10000; 

const menuIconURI = 'https://imgs.search.brave.com/O-qQeVgnU9McXQgrblY8q5AR1kWh81lp7-2xDVD1Hlc/rs:fit:500:0:0:0/g:ce/aHR0cHM6Ly9pY29u/cy52ZXJ5aWNvbi5j/b20vcG5nLzEyOC9t/aXNjZWxsYW5lb3Vz/L2JsdWUtbGluZWFy/LWljb24tMS9ubHAt/MS5wbmc'
const blockIconURI = 'https://imgs.search.brave.com/2pJkUQ4YsMPrDhYUdEhCYtHm3RD2tVnaBdnL_u6AQk4/rs:fit:500:0:0:0/g:ce/aHR0cHM6Ly9pY29u/cy52ZXJ5aWNvbi5j/b20vcG5nLzEyOC9m/aWxlLXR5cGUvY29t/cG9uZW50L2xpZ2h0/LWNvbXBvbmVudC1u/bHAucG5n'

class Scratch3ShowImageExtension {
    constructor(runtime) {
        this.runtime = runtime;
        this._viewerLanguageCode = this.getViewerLanguageCode();

        /**
         * List of supported language name and language code pairs, for use in the block menu.
         * Filled in by getInfo so it is updated when the interface language changes.
         * @type {Array.<object.<string, string>>}
         * @private
         */
        this._supportedLanguages = [];

        /**
         * A randomly selected language code, for use as the default value in the language menu.
         * Properly filled in getInfo so it is updated when the interface languages changes.
         * @type {string}
         * @private
         */
        this._randomLanguageCode = 'en';

        /**
         * The result from the most recent translation.
         * @type {string}
         * @private
         */
        this._translateResult = '';

        /**
         * The language of the text most recently translated.
         * @type {string}
         * @private
         */
        this._lastLangTranslated = '';

        /**
         * The text most recently translated.
         * @type {string}
         * @private
         */
        this._lastTextTranslated = '';
        this.positiveWords = new Map([
            // High positive words (weight: 2)
            ['excellent', 2], ['amazing', 2], ['fantastic', 2], ['wonderful', 2], 
            ['brilliant', 2], ['outstanding', 2], ['superb', 2], ['magnificent', 2],
            ['thrilled', 2], ['delighted', 2], ['ecstatic', 2], ['overjoyed', 2],
            
            // Medium positive words (weight: 1)
            ['good', 1], ['happy', 1], ['love', 1], ['like', 1], ['great', 1],
            ['nice', 1], ['pleased', 1], ['satisfied', 1], ['cheerful', 1],
            ['joyful', 1], ['peaceful', 1], ['grateful', 1], ['optimistic', 1],
            ['loving', 1], ['kind', 1], ['successful', 1], ['generous', 1],
            ['confident', 1], ['vibrant', 1], ['inspiring', 1], ['enthusiastic', 1],
            ['courageous', 1], ['harmonious', 1], ['resilient', 1], ['blissful', 1]
        ]);

        this.negativeWords = new Map([
            // High negative words (weight: 2)
            ['terrible', 2], ['awful', 2], ['horrible', 2], ['disgusting', 2],
            ['hate', 2], ['despise', 2], ['furious', 2], ['devastated', 2],
            ['miserable', 2], ['hopeless', 2], ['terrified', 2], ['desperate', 2],
            
            // Medium negative words (weight: 1)
            ['bad', 1], ['sad', 1], ['angry', 1], ['frustrated', 1], ['anxious', 1],
            ['boring', 1], ['depressed', 1], ['fearful', 1], ['annoying', 1],
            ['hateful', 1], ['jealous', 1], ['bitter', 1], ['disappointed', 1],
            ['lonely', 1], ['tired', 1], ['guilty', 1], ['upset', 1],
            ['discouraged', 1], ['hurt', 1], ['unhappy', 1], ['irritated', 1],
            ['overwhelmed', 1], ['leave', 1], ['alone', 1], ['ignore', 1], ['reject', 1],
            ['avoid', 1], ['deny', 1], ['refuse', 1], ['isolate', 1],
            ['block', 1], ['disconnect', 1], ['silence', 1], ['stop', 1],
            ['don\'t', 1], ['cannot', 1], ['won\'t', 1]
        ]);

        this.negativePhrases = new Map([
            ['leave me alone', 2],
            ["don\'t talk to me", 2],
            ["stop talking", 1],
            ["go away", 2],
            ["shut up", 2],
            ["don\'t bother", 1],
            ["stay away", 2]
        ]);

        this.positivePhrases = new Map([
            ["thank you", 1],
            ["well done", 2],
            ["good job", 2]
        ]);

        this.negativeCommands = new Set([
            'stop', 'leave', 'go', 'shut', 'don\'t', 'never'
        ]);

        // Negation words that flip sentiment
        this.negationWords = new Set(['not', 'no', 'never', 'neither', 'nor', 'none', 'nobody', 'nothing', 'nowhere']);
        
        // Intensifiers that amplify sentiment
        this.intensifiers = new Map([
            ['very', 1.5], ['extremely', 2], ['incredibly', 2], ['absolutely', 1.8],
            ['completely', 1.7], ['totally', 1.6], ['really', 1.3], ['quite', 1.2],
            ['rather', 1.1], ['pretty', 1.1], ['so', 1.4], ['too', 1.3]
        ]);

        this.lastSimilarityResult = null;
    }

    getInfo () {
        this._supportedLanguages = this._getSupportedLanguages(this.getViewerLanguageCode());
        this._randomLanguageCode = this._supportedLanguages[
            Math.floor(Math.random() * this._supportedLanguages.length)].value;

        return {
            id: 'NLP',
            name: formatMessage({
                id: 'NLP.categoryName',
                default: 'NLP',
                description: 'Name of extension that adds NLP blocks'
            }),
            blockIconURI: blockIconURI,
            menuIconURI: menuIconURI,
            color1: '#ffA500',
            color2: '#ffA500',
            blocks: [
                {
                    opcode: 'getTranslate',
                    text: formatMessage({
                        id: 'NLP.translateBlock',
                        default: 'translate [WORDS] [SLANGUAGE] to [LANGUAGE]',
                        description: 'translate some text to a different language'
                    }),
                    blockType: BlockType.REPORTER,
                    arguments: {
                        WORDS: {
                            type: ArgumentType.STRING,
                            defaultValue: formatMessage({
                                id: 'NLP.defaultTextToTranslate',
                                default: 'hello',
                                description: 'hello: the default text to translate'
                            })
                        },
                        SLANGUAGE: {
                            type: ArgumentType.STRING,
                            menu: 'languages',
                            defaultValue: 'en'
                        },
                        LANGUAGE: {
                            type: ArgumentType.STRING,
                            menu: 'languages',
                            defaultValue: this._randomLanguageCode
                        }
                    }
                },
                {
                    opcode: 'lexicalAnalysis',
                    text: formatMessage({
                        id: 'NLP.lexicalBlock',
                        default: 'Lexical Analysis Text: [TEXT]',
                        description: 'lexical analysis of text'
                    }),
                    blockType: BlockType.REPORTER,
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: formatMessage({
                                id: 'translate.defaultTextToTranslate',
                                default: 'Dogs and Cats',
                                description: 'How are you?: the default text to translate'
                            })
                        }
                    }
                },
                {
                    opcode: 'sentimentAnalysis',
                    text: formatMessage({
                        id: 'NLP.sentimentBlock',
                        default: 'Sentiment Analysis Text: [TEXT]',
                        description: 'Sentiment analysis of text'
                    }),
                    blockType: BlockType.REPORTER,
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: formatMessage({
                                id: 'sentiment.defaultTextToAnalyze',
                                default: 'I love programming!',
                                description: 'The default text to analyze for sentiment'
                            })
                        }
                    }
                },
                {
                    opcode: 'semanticSimilarityAnalysis',
                    text: 'Semantic similarity analysis text1: [TEXT1] text2: [TEXT2]',
                    blockType: BlockType.COMMAND,
                    arguments: {
                        TEXT1: {
                            type: ArgumentType.STRING,
                            defaultValue: 'semantic analysis'
                        },
                        TEXT2: {
                            type: ArgumentType.STRING,
                            defaultValue: 'sentiment analysis'
                        }
                    }
                },
                {
                    opcode: 'semanticSimilarityResult',
                    text: 'Semantic similarity analysis result',
                    blockType: BlockType.REPORTER
                }
            ],
            menus: {
                languages: {
                    acceptReporters: true,
                    items: this._supportedLanguages
                }
            }
        };
    }

    lexicalAnalysis(args) {
        try {
            const userText = args.TEXT;
            if (!userText || typeof userText !== 'string') {
                throw new Error('Invalid or missing TEXT parameter');
            }

            // Enhanced tokenization and POS analysis
            const doc = nlp(userText);
            const analysis = {};
            let wordCount = 0;
            const posStats = {};

            doc.document[0].forEach(element => {
                const word = element.text.toLowerCase(); // Normalize case
                const posTags = [...element.tags];
                wordCount++;

                let pos = 'unknown';
                if (posTags.length > 0) {
                    // Priority-based POS tag selection
                    if (posTags.includes('Pronoun')) {
                        pos = 'Pronoun';
                    } else if (posTags.includes('Verb')) {
                        pos = 'Verb';
                    } else if (posTags.includes('Noun')) {
                        pos = 'Noun';
                    } else if (posTags.includes('Adjective')) {
                        pos = 'Adjective';
                    } else if (posTags.includes('Adverb')) {
                        pos = 'Adverb';
                    } else {
                        pos = posTags[0];
                    }
                }

                analysis[word] = pos;
                
                // Track POS statistics
                posStats[pos] = (posStats[pos] || 0) + 1;
            });

            const result = {
                wordAnalysis: analysis,
                statistics: {
                    totalWords: wordCount,
                    uniqueWords: Object.keys(analysis).length,
                    posDistribution: posStats
                }
            };

            console.log('Lexical Analysis:', result);
            return JSON.stringify(analysis);
        } catch (error) {
            console.error('Error in lexicalAnalysis:', error);
            return JSON.stringify({ error: error.message });
        }
    }

    sentimentAnalysis(args) {
      try {
          const userText = args.TEXT;
          if (!userText || typeof userText !== 'string') {
              return 'Error: Invalid or missing TEXT parameter';
          }

          const cleanText = userText.toLowerCase().replace(/[^\w\s]/g, ' ');
          const words = cleanText.split(/\s+/).filter(word => word.length > 0);

          let sentimentScore = 0;
          let positiveCount = 0;
          let negativeCount = 0;
          let neutralCount = 0;

          // --- Step 1: Phrase-level sentiment (check before looping words) ---
          for (const [phrase, score] of this.negativePhrases) {
              if (cleanText.includes(phrase)) {
                  sentimentScore -= score;
                  negativeCount++;
              }
          }

          for (const [phrase, score] of this.positivePhrases || []) {
              if (cleanText.includes(phrase)) {
                  sentimentScore += score;
                  positiveCount++;
              }
          }

          // --- Step 2: Word-level sentiment ---
          for (let i = 0; i < words.length; i++) {
              const word = words[i];
              let wordScore = 0;
              let isNegated = false;
              let intensifierMultiplier = 1;

              // Check for negation in the previous 2 words
              for (let j = Math.max(0, i - 2); j < i; j++) {
                  if (this.negationWords.has(words[j])) {
                      isNegated = true;
                      break;
                  }
              }

              // Check for intensifier in the previous word
              if (i > 0 && this.intensifiers.has(words[i - 1])) {
                  intensifierMultiplier = this.intensifiers.get(words[i - 1]);
              }

              // Word sentiment scoring
              if (this.positiveWords.has(word)) {
                  wordScore = this.positiveWords.get(word) * intensifierMultiplier;
                  if (isNegated) wordScore = -wordScore;
                  positiveCount += isNegated ? 0 : 1;
                  negativeCount += isNegated ? 1 : 0;
              } else if (this.negativeWords.has(word)) {
                  wordScore = -this.negativeWords.get(word) * intensifierMultiplier;
                  if (isNegated) wordScore = -wordScore;
                  negativeCount += isNegated ? 0 : 1;
                  positiveCount += isNegated ? 1 : 0;
              } else {
                  neutralCount++;
              }

              sentimentScore += wordScore;
          }

          // --- Step 3: Normalize sentiment score ---
          const normalizedScore = sentimentScore / Math.max(words.length, 1);

          // --- Step 4: Determine sentiment ---
          let sentiment;
          let confidence;

          if (normalizedScore > 0.1) {
              sentiment = 'positive';
              confidence = Math.min(normalizedScore * 100, 100);
          } else if (normalizedScore < -0.1) {
              sentiment = 'negative';
              confidence = Math.min(Math.abs(normalizedScore) * 100, 100);
          } else {
              sentiment = 'neutral';
              confidence = 100 - Math.abs(normalizedScore) * 100;
          }

          return sentiment;

      } catch (error) {
          return `Error: ${error.message}`;
      }
  }

    // Returns just the sentiment (positive/negative/neutral)
    getSentiment(args) {
        try {
            const result = this.sentimentAnalysis(args);
            if (result.startsWith('Error:')) return result;
            
            // Extract just the sentiment from the full result
            const sentiment = result.split(' (')[0].replace('Sentiment: ', '');
            return sentiment;
        } catch (error) {
            return `Error: ${error.message}`;
        }
    }

    // Returns just the confidence percentage
    getSentimentConfidence(args) {
        try {
            const result = this.sentimentAnalysis(args);
            if (result.startsWith('Error:')) return result;
            
            // Extract confidence percentage
            const match = result.match(/\((\d+)% confidence\)/);
            return match ? match[1] + '%' : 'Unknown';
        } catch (error) {
            return `Error: ${error.message}`;
        }
    }

    // Returns similarity as a percentage string
    semanticSimilarityAnalysis(args) {
        try {
            const text1 = args.TEXT1;
            const text2 = args.TEXT2;

            if (!text1 || !text2 || typeof text1 !== 'string' || typeof text2 !== 'string') {
                return 'Error: Both TEXT1 and TEXT2 parameters are required and must be strings';
            }

            // Multiple similarity metrics for better accuracy
            const similarities = this.calculateMultipleSimilarities(text1, text2);
            
            // Weighted average of different similarity metrics
            const finalScore = (
                similarities.jaccard * 0.3 +
                similarities.cosine * 0.4 +
                similarities.overlap * 0.2 +
                similarities.dice * 0.1
            );

            const overallSimilarity = Math.round(finalScore * 10000) / 100;
            
            const result = {
                overallSimilarity: overallSimilarity,
                metrics: {
                    jaccard: Math.round(similarities.jaccard * 10000) / 100,
                    cosine: Math.round(similarities.cosine * 10000) / 100,
                    overlap: Math.round(similarities.overlap * 10000) / 100,
                    dice: Math.round(similarities.dice * 10000) / 100
                }
            };

            this.lastSimilarityResult = result;
            
            // Return formatted string
            return `Similarity: ${overallSimilarity}% | Jaccard: ${result.metrics.jaccard}% | Cosine: ${result.metrics.cosine}% | Overlap: ${result.metrics.overlap}% | Dice: ${result.metrics.dice}%`;
            
        } catch (error) {
            this.lastSimilarityResult = { error: error.message };
            return `Error: ${error.message}`;
        }
    }

    // Returns just the overall similarity percentage
    getSimilarityPercentage(args) {
        try {
            const result = this.semanticSimilarityAnalysis(args);
            if (result.startsWith('Error:')) return result;
            
            // Extract just the percentage
            const match = result.match(/Similarity: ([\d.]+)%/);
            return match ? match[1] + '%' : 'Unknown';
        } catch (error) {
            return `Error: ${error.message}`;
        }
    }

    // Block to retrieve the result of the last semantic similarity analysis
    semanticSimilarityResult() {
        try {
            if (!this.lastSimilarityResult) {
                return 'No analysis performed yet';
            }
            
            if (this.lastSimilarityResult.error) {
                return `Error: ${this.lastSimilarityResult.error}`;
            }
            
            return `${this.lastSimilarityResult.overallSimilarity}%`;
        } catch (error) {
            return `Error: ${error.message}`;
        }
    }

    // Helper function to calculate semantic similarity
    calculateMultipleSimilarities(text1, text2) {
        // Preprocess texts
        const processText = (text) => {
            return text.toLowerCase()
                .replace(/[^\w\s]/g, ' ')
                .split(/\s+/)
                .filter(word => word.length > 1); // Remove single characters
        };

        const words1 = processText(text1);
        const words2 = processText(text2);
        
        const set1 = new Set(words1);
        const set2 = new Set(words2);

        // 1. Jaccard Similarity
        const intersection = new Set([...set1].filter(x => set2.has(x)));
        const union = new Set([...set1, ...set2]);
        const jaccard = union.size > 0 ? intersection.size / union.size : 0;

        // 2. Cosine Similarity (using word frequencies)
        const allWords = [...union];
        const vector1 = allWords.map(word => words1.filter(w => w === word).length);
        const vector2 = allWords.map(word => words2.filter(w => w === word).length);
        
        const dotProduct = vector1.reduce((sum, val, i) => sum + (val * vector2[i]), 0);
        const magnitude1 = Math.sqrt(vector1.reduce((sum, val) => sum + (val * val), 0));
        const magnitude2 = Math.sqrt(vector2.reduce((sum, val) => sum + (val * val), 0));
        
        const cosine = (magnitude1 && magnitude2) ? dotProduct / (magnitude1 * magnitude2) : 0;

        // 3. Overlap Coefficient
        const overlap = Math.min(set1.size, set2.size) > 0 ? intersection.size / Math.min(set1.size, set2.size) : 0;

        // 4. Dice Coefficient
        const dice = (set1.size + set2.size) > 0 ? (2 * intersection.size) / (set1.size + set2.size) : 0;

        return { jaccard, cosine, overlap, dice };
    }

   _getSupportedLanguages (code) {
        // Extended language list for MyMemory API including Indian languages and more
        const myMemoryLanguages = [
            // Major European Languages
            { text: 'English', value: 'en' },
            { text: 'Spanish', value: 'es' },
            { text: 'French', value: 'fr' },
            { text: 'German', value: 'de' },
            { text: 'Italian', value: 'it' },
            { text: 'Portuguese', value: 'pt' },
            { text: 'Russian', value: 'ru' },
            { text: 'Dutch', value: 'nl' },
            { text: 'Polish', value: 'pl' },
            { text: 'Swedish', value: 'sv' },
            { text: 'Norwegian', value: 'no' },
            { text: 'Danish', value: 'da' },
            { text: 'Finnish', value: 'fi' },
            { text: 'Greek', value: 'el' },
            { text: 'Czech', value: 'cs' },
            { text: 'Hungarian', value: 'hu' },
            { text: 'Romanian', value: 'ro' },
            { text: 'Bulgarian', value: 'bg' },
            { text: 'Croatian', value: 'hr' },
            { text: 'Slovak', value: 'sk' },
            { text: 'Ukrainian', value: 'uk' },
            
            // Indian Languages
            { text: 'Hindi', value: 'hi' },
            { text: 'Telugu', value: 'te' },
            { text: 'Tamil', value: 'ta' },
            { text: 'Bengali', value: 'bn' },
            { text: 'Gujarati', value: 'gu' },
            { text: 'Kannada', value: 'kn' },
            { text: 'Malayalam', value: 'ml' },
            { text: 'Marathi', value: 'mr' },
            { text: 'Punjabi', value: 'pa' },
            { text: 'Urdu', value: 'ur' },
            { text: 'Odia', value: 'or' },
            { text: 'Assamese', value: 'as' },
            
            // East Asian Languages
            { text: 'Chinese (Simplified)', value: 'zh' },
            { text: 'Chinese (Traditional)', value: 'zh_HANT' },
            { text: 'Japanese', value: 'ja' },
            { text: 'Korean', value: 'ko' },
            
            // Southeast Asian Languages
            { text: 'Indonesian', value: 'id' },
            { text: 'Malaysian', value: 'ms' },
            { text: 'Thai', value: 'th' },
            { text: 'Vietnamese', value: 'vi' },
            { text: 'Filipino', value: 'tl' },
            
            // Middle Eastern Languages
            { text: 'Arabic', value: 'ar' },
            { text: 'Persian', value: 'fa' },
            { text: 'Hebrew', value: 'he' },
            { text: 'Turkish', value: 'tr' },
            
            // African Languages
            { text: 'Swahili', value: 'sw' },
            { text: 'Afrikaans', value: 'af' },
            { text: 'Amharic', value: 'am' },
            { text: 'Yoruba', value: 'yo' },
            
            // Other Languages
            { text: 'Esperanto', value: 'eo' },
            { text: 'Latin', value: 'la' },
            { text: 'Irish', value: 'ga' },
            { text: 'Welsh', value: 'cy' },
            { text: 'Basque', value: 'eu' },
            { text: 'Catalan', value: 'ca' },
            { text: 'Slovenian', value: 'sl' },
            { text: 'Estonian', value: 'et' },
            { text: 'Latvian', value: 'lv' },
            { text: 'Lithuanian', value: 'lt' },
            { text: 'Maltese', value: 'mt' },
            { text: 'Icelandic', value: 'is' }
        ];

        // Sort alphabetically by language name for better user experience
        return myMemoryLanguages.sort((a, b) => a.text.localeCompare(b.text));
    }

    getViewerLanguage () {
        this._viewerLanguageCode = this.getViewerLanguageCode();
        const names = languageNames.menuMap[this._viewerLanguageCode];
        let langNameObj = names.find(obj => obj.code === this._viewerLanguageCode);

        // If we don't have a name entry yet, try looking it up via the Google langauge
        // code instead of Scratch's (e.g. for es-419 we look up es to get espanol)
        if (!langNameObj && languageNames.scratchToGoogleMap[this._viewerLanguageCode]) {
            const lookupCode = languageNames.scratchToGoogleMap[this._viewerLanguageCode];
            langNameObj = names.find(obj => obj.code === lookupCode);
        }

        let langName = this._viewerLanguageCode;
        if (langNameObj) {
            langName = langNameObj.name;
        }
        return langName;
    }

    getViewerLanguageCode () {
        const locale = formatMessage.setup().locale;
        const viewerLanguages = [locale].concat(navigator.languages);
        const languageKeys = Object.keys(languageNames.menuMap);
        // Return the first entry in viewerLanguages that matches
        // one of the available language keys.
        const languageCode = viewerLanguages.reduce((acc, lang) => {
            if (acc) {
                return acc;
            }
            if (languageKeys.indexOf(lang.toLowerCase()) > -1) {
                return lang;
            }
            return acc;
        }, '') || 'en';
    
        return languageCode.toLowerCase();
    }

    getLanguageCodeFromArg(arg) {
      const languageArg = Cast.toString(arg).toLowerCase();

      // First check against supportedLanguages values (like "zh", "hi", "es")
      const matchByCode = this._supportedLanguages.find(l => l.value.toLowerCase() === languageArg);
      if (matchByCode) return matchByCode.value;

      // Then check by display text (like "Chinese (Simplified)")
      const matchByName = this._supportedLanguages.find(l => l.text.toLowerCase() === languageArg);
      if (matchByName) return matchByName.value;

      // Then check Scratch language maps
      if (Object.prototype.hasOwnProperty.call(languageNames.menuMap, languageArg)) {
          return languageArg;
      }
      if (Object.prototype.hasOwnProperty.call(languageNames.nameMap, languageArg)) {
          return languageNames.nameMap[languageArg];
      }

      if (languageNames.previouslySupported.indexOf(languageArg) !== -1) {
          return languageArg;
      }

      return 'en'; // fallback
    }

    getTranslate(args) {
      // If the text contains only digits 0-9 and nothing else, return it without
      // making a request.
      if (/^\d+$/.test(args.WORDS)) return Promise.resolve(args.WORDS);

      // Don't remake the request if we already have the value.
      if (this._lastTextTranslated === args.WORDS &&
          this._lastLangTranslated === args.LANGUAGE) {
          return Promise.resolve(this._translateResult);
      }

      const sourceLang = this.getLanguageCodeFromArg(args.SLANGUAGE);
      const targetLang = this.getLanguageCodeFromArg(args.LANGUAGE);

      // Encode text for URL
      const encodedText = encodeURIComponent(args.WORDS);

      // Lingva API endpoint
      const lingvaUrl = `https://lingva.ml/api/v1/${sourceLang}/${targetLang}/${encodedText}`;

      console.log("Making Lingva translation request to:", lingvaUrl);

      const tempThis = this;
      const translatePromise = fetchWithTimeout(lingvaUrl, {
          method: "GET",
          headers: {
              "Accept": "application/json",
              "User-Agent": "Scratch-NLP-Extension"
          }
      }, serverTimeoutMs)
          .then(response => {
              if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`);
              }
              return response.json();
          })
          .then(data => {
              console.log("Full Lingva response:", data);

              if (!data.translation) {
                  throw new Error("No translation field in response");
              }

              const translated = data.translation;

              // Cache result
              tempThis._translateResult = translated;
              tempThis._lastTextTranslated = args.WORDS;
              tempThis._lastLangTranslated = args.LANGUAGE;

              console.log("Final translation result:", translated);
              return translated;
          })
          .catch(err => {
              log.warn(`error fetching translate result! ${err}`);
              console.error("Translation error details:", err);
              return args.WORDS; // Fallback to original text
          });

      return translatePromise;
  }
}

module.exports = Scratch3ShowImageExtension;